import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Flex, Text, Image, Spinner } from "@chakra-ui/react";
import PostItemComponent from "../components/PostComponent/PostItemComponent";
import SpinnerComponent from "../components/Common/SpinnerComponent";
import { consumeApi } from "../utils/tools";
import { PostData } from "../definitions/interfaces/GlobalInterfaces";
import { appStore } from "../zustand/globalStore";
import i18n from "../i18n/i18n";
import { colors } from "../definitions/constants/GlobalStyles";

export default function UserProfileScreen() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { userData } = appStore((state) => state);

    const [posts, setPosts] = useState<PostData[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [profileData, setProfileData] = useState<any>(null);

    // Track previous id to reset state when visited user changes
    const lastViewedId = useRef<string | null>(null);

    const fetchPosts = useCallback(async (userId: string, pageNum: number, isLoadMore = false) => {
        try {
            if (!isLoadMore) setLoading(true);
            else setLoadingMore(true);

            const [response, responseShared] = await Promise.all([
                consumeApi(`posts/get_posts_by_user`, "POST", {
                    user_id: userId,
                    page: pageNum,
                    limit: 10,
                    post_type: "user_post"
                }),
                consumeApi(`posts/get_posts_by_user`, "POST", {
                    user_id: userId,
                    page: pageNum,
                    limit: 10,
                    post_type: "shared_post"
                })
            ]);

            // Combine both user_post and shared_post temporarily (or we can use two tabs)
            // For simplicity we will merge and sort them by date (created_at) descending or rate
            let combined: PostData[] = [];
            if (response && Array.isArray(response.data)) {
                combined = [...combined, ...response.data];
            }
            if (responseShared && Array.isArray(responseShared.data)) {
                combined = [...combined, ...responseShared.data];
            }

            // Basic sorting descending by id to mimic timeline
            combined.sort((a, b) => b.id - a.id);

            // Remove duplicates if any logic anomaly occurs
            const uniquePosts = combined.reduce((acc, current) => {
                const x = acc.find((item: PostData) => item.id === current.id);
                if (!x) {
                    return acc.concat([current]);
                } else {
                    return acc;
                }
            }, [] as PostData[]);

            if (uniquePosts.length === 0) {
                setHasMore(false);
            } else {
                setHasMore(uniquePosts.length === 20); // rough estimate if fully loaded both sets
            }

            setPosts((prev) => (isLoadMore ? [...prev, ...uniquePosts] : uniquePosts));
        } catch (error) {
            console.error("Error fetching user posts", error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        const userIdToFetch = id || userData?.id;
        if (!userIdToFetch) {
            navigate("/");
            return;
        }

        if (lastViewedId.current !== userIdToFetch) {
            setPosts([]);
            setPage(1);
            setHasMore(true);
            setProfileData(null);
            lastViewedId.current = userIdToFetch as string;

            // Fetch User Profile
            consumeApi(`users/get_user_by_id`, "POST", { user_id: userIdToFetch })
                .then(res => {
                    if (res && res.status === "success" && res.user_data) {
                        setProfileData(res.user_data);
                    }
                })
                .catch(err => console.error("Error fetching user profile", err));

            fetchPosts(userIdToFetch as string, 1, false);
        }
    }, [id, userData, navigate, fetchPosts]);

    const handleLoadMore = () => {
        if (!loading && !loadingMore && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchPosts(lastViewedId.current as string, nextPage, true);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            const bottom = Math.ceil(window.innerHeight + window.scrollY) >= document.documentElement.scrollHeight - 50;
            if (bottom) {
                handleLoadMore();
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [loading, loadingMore, hasMore, page, handleLoadMore]);

    if (loading && page === 1) {
        return <SpinnerComponent spinnerKey="fullscreen" />;
    }

    return (
        <Box width="100%" pb={10}>
            {/* User Profile Header */}
            <Box mb={6} position="relative" bg={colors.figmaColors.grey0} pb={6} shadow="sm">
                <Box h="150px" w="100%" bg={colors.figmaColors.secColor} />
                <Flex direction="column" align="center" mt="-60px">
                    {profileData?.image_url ? (
                        <Image
                            src={profileData.image_url}
                            alt="Profile"
                            boxSize="120px"
                            borderRadius="full"
                            border="4px solid white"
                            bg="gray.200"
                            objectFit="cover"
                        />
                    ) : (
                        <Box boxSize="120px" borderRadius="full" border="4px solid white" bg="gray.300" />
                    )}
                    <Text fontSize="2xl" fontWeight="bold" mt={3} color={colors.figmaColors.black}>
                        {profileData?.firstname} {profileData?.lastname}
                    </Text>
                    <Text fontSize="md" color={colors.figmaColors.grey3}>
                        @{profileData?.username}
                    </Text>
                </Flex>
            </Box>

            {posts.length === 0 ? (
                <Box pt={10} textAlign="center" color="gray.500">
                    {i18n.t("noResults")}
                </Box>
            ) : (
                <Flex direction="column" gap={4}>
                    {posts.map((post: any) => (
                        <PostItemComponent key={post.id} post={post} />
                    ))}
                    {loadingMore && (
                        <Box py={4}>
                            <SpinnerComponent spinnerKey="section" />
                        </Box>
                    )}
                </Flex>
            )}
        </Box>
    );
}
